import { useState, useEffect } from 'react';
import { Save, Plus, Trash2 } from 'lucide-react';
import { configAPI } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

export default function AdminConfig() {
  const [config, setConfig] = useState({
    sobre_texto: '',
    missao: '',
    visao: '',
    valores: [],
    telefone: '',
    email: '',
    endereco: '',
    instagram: '',
    tiktok: '',
    facebook: '',
    youtube: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newValor, setNewValor] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await configAPI.obter();
        setConfig(res.data);
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await configAPI.atualizar(config);
      toast({ title: "Configurações salvas com sucesso!" });
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    setConfig({ ...config, [e.target.name]: e.target.value });
  };

  const addValor = () => {
    if (newValor.trim()) {
      setConfig({ ...config, valores: [...config.valores, newValor.trim()] });
      setNewValor('');
    }
  };

  const removeValor = (index) => {
    const newValores = config.valores.filter((_, i) => i !== index);
    setConfig({ ...config, valores: newValores });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-config">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Configurações</h1>
        <p className="text-slate-600 mt-1">Configure as informações do site</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Sobre */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Sobre Nós</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Texto Principal
              </label>
              <textarea
                name="sobre_texto"
                value={config.sobre_texto}
                onChange={handleChange}
                rows={4}
                data-testid="config-sobre-texto"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 resize-none"
                placeholder="Texto sobre a associação..."
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Missão</label>
              <textarea
                name="missao"
                value={config.missao}
                onChange={handleChange}
                rows={3}
                data-testid="config-missao"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 resize-none"
                placeholder="Missão da associação..."
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Visão</label>
              <textarea
                name="visao"
                value={config.visao}
                onChange={handleChange}
                rows={3}
                data-testid="config-visao"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 resize-none"
                placeholder="Visão da associação..."
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Valores</label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newValor}
                  onChange={(e) => setNewValor(e.target.value)}
                  data-testid="config-novo-valor"
                  className="flex-1 px-4 py-2 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="Adicionar valor..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addValor())}
                />
                <button
                  type="button"
                  onClick={addValor}
                  data-testid="add-valor-btn"
                  className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
                >
                  <Plus size={20} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {config.valores.map((valor, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm"
                  >
                    {valor}
                    <button
                      type="button"
                      onClick={() => removeValor(index)}
                      className="hover:text-red-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Contato */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Informações de Contato</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">E-mail</label>
              <input
                type="email"
                name="email"
                value={config.email}
                onChange={handleChange}
                data-testid="config-email"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                placeholder="contato@acecfeminino.com.br"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Telefone</label>
              <input
                type="tel"
                name="telefone"
                value={config.telefone}
                onChange={handleChange}
                data-testid="config-telefone"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                placeholder="(00) 00000-0000"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-bold text-slate-700 mb-2">Endereço</label>
              <input
                type="text"
                name="endereco"
                value={config.endereco}
                onChange={handleChange}
                data-testid="config-endereco"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                placeholder="Canaã dos Carajás, Pará, Brasil"
              />
            </div>
          </div>
        </div>

        {/* Redes Sociais */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Redes Sociais</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Instagram</label>
              <input
                type="url"
                name="instagram"
                value={config.instagram}
                onChange={handleChange}
                data-testid="config-instagram"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                placeholder="https://instagram.com/acecfeminino"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">TikTok</label>
              <input
                type="url"
                name="tiktok"
                value={config.tiktok}
                onChange={handleChange}
                data-testid="config-tiktok"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                placeholder="https://tiktok.com/@acec.feminino"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Facebook</label>
              <input
                type="url"
                name="facebook"
                value={config.facebook}
                onChange={handleChange}
                data-testid="config-facebook"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                placeholder="https://facebook.com/acecfeminino"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">YouTube</label>
              <input
                type="url"
                name="youtube"
                value={config.youtube}
                onChange={handleChange}
                data-testid="config-youtube"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                placeholder="https://youtube.com/@acecfeminino"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="save-config-btn"
            className="bg-blue-700 text-white px-8 py-3 rounded-lg font-bold flex items-center gap-2 hover:bg-blue-800 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={20} />
                Salvar Configurações
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
